import {createApp, h} from 'vue'
import {NConfigProvider} from 'naive-ui'
import MinisitesInfo from '@shared-vue-components/components/asiagoit-minisites-info/MinisitesInfo.vue'
import {getSeasonColors} from '~/default/scripts/utils/generic'

const locale = WC.lang
const colors = getSeasonColors(4) // always blue

function createElement(
  minisite,
  businessHoursData,
  minisitesUpdatesData,
  variant = 'full',
  otherHoursAsAnchor = false
) {
  const element = document.createElement('div')

  element.setAttribute('data-cms-vue-component', 'minisites-info')
  element.classList.add('minisites-info', `minisites-info--${variant}`)

  createApp({
    setup() {
      return () =>
        h(
          NConfigProvider,
          {
            preflightStyleDisabled: true,
            abstract: true,
            themeOverrides: {
              common: {
                primaryColor: colors.main,
                primaryColorHover: colors.lightest,
                primaryColorPressed: colors.lighter,
                primaryColorSuppl: colors.darker,
              },
            },
          },
          h(MinisitesInfo, {
            businessHoursData: businessHoursData,
            minisitesUpdatesData: minisitesUpdatesData,
            variant,
            locale,
            minisite,
            otherHoursAsAnchor,
          })
        )
    },
  }).mount(element)

  return element
}

// TODO add &language=${locale}
async function getBusinessHoursData(minisites) {
  try {
    return await (
      await fetch(
        `https://business-hours-api.webcloud.cloud/v1/?minisites=${minisites}&language=${locale}`
      )
    ).json()
  } catch (error) {
    console.error(error)
  }
}

async function getMinisitesUpdatesData(minisites) {
  try {
    function buildNozomiUrl(collection, { filter, deep, fields = '*.*', limit = -1, sort }) {
      const params = new URLSearchParams()

      if (filter) {
        params.set('filter', JSON.stringify(filter))
      }

      if (deep) {
        params.set('deep', JSON.stringify(deep))
      }

      if (fields) {
        params.set('fields', fields)
      }

      if (limit) {
        params.set('limit', String(limit))
      }

      if (sort) {
        params.set('sort', sort)
      }

      return `https://nozomi.asiago.it/items/${collection}?${params.toString()}`
    }

    async function readNozomiItems(collection, options) {
      const response = await fetch(buildNozomiUrl(collection, options))
      const json = await response.json()

      if (!response.ok) {
        console.error(`Errore lettura ${collection}`, json)
        return []
      }

      return json.data ?? []
    }

    const updatesDeep = {
      translations: {
        _filter: {
          languages_code: {
            _eq: locale,
          },
        },
      },
    }

    const ownUpdates = await readNozomiItems('minisites_updates', {
      filter: {
        minisite_id: {
          _in: minisites,
        },
      },
      deep: updatesDeep,
      fields: '*.*',
      limit: -1,
      sort: '-sort_index,id',
    })

    const sharedRows = await readNozomiItems('minisites_updates_minisites', {
      filter: {
        minisite_id: {
          _in: minisites,
        },
      },
      fields: 'id,minisite_id,minisites_updates_id',
      limit: -1,
    })

    const sharedMinisitesByUpdateId = sharedRows.reduce((acc, row) => {
      const updateId = row.minisites_updates_id?.id ?? row.minisites_updates_id

      if (!updateId) {
        return acc
      }

      acc[String(updateId)] ??= []
      acc[String(updateId)].push(String(row.minisite_id))

      return acc
    }, {})

    const sharedUpdateIds = [
      ...new Set(
        sharedRows
          .map(row => row.minisites_updates_id?.id ?? row.minisites_updates_id)
          .filter(Boolean)
      ),
    ]

    const sharedUpdates = sharedUpdateIds.length
      ? await readNozomiItems('minisites_updates', {
        filter: {
          id: {
            _in: sharedUpdateIds,
          },
        },
        deep: updatesDeep,
        fields: '*.*',
        limit: -1,
        sort: '-sort_index,id',
      })
      : []

    return Object.values(
      [...ownUpdates, ...sharedUpdates].reduce((acc, update) => {
        acc[update.id] = {
          ...update,
          shared_minisite_ids: sharedMinisitesByUpdateId[String(update.id)] ?? [],
        }

        return acc
      }, {})
    )
  } catch (error) {
    console.error(error)
  }
}

;(async () => {
  const minisitesContainers = Array.from(
    document.querySelectorAll('[data-stati-apertura-chiusura-cnt]')
  )

  if (!minisitesContainers.length) {
    return
  }

  const minisitesIds = minisitesContainers.map(item => item.dataset.minisiteId)

  let businessHoursAllData = getBusinessHoursData(minisitesIds)
  let minisitesUpdatesAllData = getMinisitesUpdatesData(minisitesIds)

  await Promise.all([businessHoursAllData, minisitesUpdatesAllData])

  businessHoursAllData = await businessHoursAllData
  minisitesUpdatesAllData = await minisitesUpdatesAllData

  if (!businessHoursAllData?.length && !minisitesUpdatesAllData?.length) {
    return
  }

  if (businessHoursAllData.error) {
    console.error(businessHoursAllData.error)
    return
  }

  for (const minisiteContainer of minisitesContainers) {
    const minisiteId = parseInt(minisiteContainer.dataset.minisiteId, 10)

    const businessHoursData = businessHoursAllData?.find(
      item => String(item.minisite) === String(minisiteId)
    )

    const minisitesUpdatesData = minisitesUpdatesAllData?.filter(
      item =>
        String(item.minisite_id) === String(minisiteId) ||
        item.shared_minisite_ids?.includes(String(minisiteId))
    )

    if (!businessHoursData?.services?.length && !minisitesUpdatesData?.length) {
      continue
    }

    const minisite = WC.PageVars.minisites?.find(minisite => minisite.id === minisiteId)

    if (!minisite) {
      continue
    }

    function mountElement() {
      const create = (variant, otherHoursAsAnchor) =>
        createElement(
          minisite,
          businessHoursData,
          minisitesUpdatesData,
          variant,
          otherHoursAsAnchor
        )

      // article-box (itinerari, webcam, etc.)
      minisiteContainer
        .querySelector('.minisite-article-links__item--title')
        ?.after(create('article-box'))

      if (WC.idPaginaMenuMinisito === 1) {
        // testata minisito
        minisiteContainer.querySelector('.misi-flex-header')?.after(create('ultra-compact', true))

        // description minisito
        minisiteContainer.parentElement
          ?.querySelector('.minisite-description--long')
          ?.before(create())
      } else {
        // testata minisito
        minisiteContainer.querySelector('.misi-flex-header')?.after(create('compact'))
      }

      // miniature minisiti
      minisiteContainer
        .querySelector(
          '.minisites-view--full .minisites-view-item__header, .minisites-view--compact .minisites-view-item__header, .ArticleAlbergo .boxTitolo'
        )
        ?.after(create('ultra-compact'))
    }

    if (window.cmsVueComponentsReady) {
      mountElement()
    } else {
      document.addEventListener('cmsVueComponentsReady', mountElement)
    }
  }
})()
